/**
 * iapBridge.js — Client-side bridge to the native iOS StoreKit 2 layer.
 *
 * The PWABuilder iOS wrapper hosts our React app inside a WKWebView. To
 * initiate an In-App Purchase we cannot use JavaScript alone — StoreKit
 * requires native Swift code. We solve this by installing a small Swift
 * class (SelahIAPBridge.swift) on the WebView's WKUserContentController
 * that exposes exactly three actions to JS:
 *
 *   - selahIAP.getProducts()     → returns [{id, price, title, description}]
 *   - selahIAP.purchase(productId) → initiates Apple purchase UI, returns
 *                                    a signed JWS transaction on success
 *   - selahIAP.restore()         → returns array of signed JWS transactions
 *                                    for every current entitlement
 *
 * The bridge is invoked via
 *     window.webkit.messageHandlers.selahIAP.postMessage({...})
 * and results come back via a JS callback the native side stores as
 *     window.__selahIAPResolvers[requestId](result)
 *
 * On non-iOS platforms (web / Android / Windows), the bridge is absent.
 * All exported functions gracefully return { available: false } so the
 * caller can fall back to Stripe checkout.
 *
 * Apple Guideline 3.1.1 compliance
 * --------------------------------
 * This module NEVER surfaces an external checkout URL when running on iOS.
 * All purchase flows go exclusively through Apple's IAP UI.
 */

// Global registry of pending native-bridge requests. The native code posts
// a message back to JS with { requestId, result }, and we resolve the
// matching Promise from this map.
if (typeof window !== 'undefined' && !window.__selahIAPResolvers) {
  window.__selahIAPResolvers = {};
}

/**
 * True when the app is running inside the iOS wrapper AND the native
 * SelahIAPBridge has been installed on the WebView.
 */
export function isNativeIAPAvailable() {
  if (typeof window === 'undefined') return false;
  const wk = window.webkit;
  if (!wk || !wk.messageHandlers) return false;
  return typeof wk.messageHandlers.selahIAP !== 'undefined';
}

/**
 * Send a request to the native layer and wait for its response.
 * The native side must call `window.__selahIAPResolvers[requestId](result)`
 * with a JSON-serialisable result.
 */
function sendToNative(action, params = {}, timeoutMs = 60_000) {
  // Fire a diagnostic breadcrumb if the on-screen debug panel is mounted.
  // Safe on all platforms — undefined when the panel isn't installed.
  if (typeof window !== 'undefined' && typeof window.__iapDebug === 'function') {
    window.__iapDebug(
      `sendToNative(${action}) → available=${isNativeIAPAvailable()}`
    );
  }

  if (!isNativeIAPAvailable()) {
    return Promise.resolve({ available: false, error: 'native_bridge_unavailable' });
  }

  return new Promise((resolve) => {
    const requestId = `iap_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    const registry = window.__selahIAPResolvers;

    // Set up a timeout so a broken native handler cannot hang the UI.
    const timer = setTimeout(() => {
      if (registry[requestId]) {
        delete registry[requestId];
        if (typeof window.__iapDebug === 'function') {
          window.__iapDebug(`TIMEOUT after ${timeoutMs}ms for ${action}`);
        }
        resolve({ available: true, ok: false, error: 'native_timeout' });
      }
    }, timeoutMs);

    registry[requestId] = (result) => {
      clearTimeout(timer);
      delete registry[requestId];
      if (typeof window.__iapDebug === 'function') {
        try {
          window.__iapDebug(
            `native reply(${action}) → ${JSON.stringify(result).slice(0, 200)}`
          );
        } catch (_e) {
          window.__iapDebug(`native reply(${action}) → [unserialisable]`);
        }
      }
      resolve({ available: true, ok: true, ...(result || {}) });
    };

    try {
      window.webkit.messageHandlers.selahIAP.postMessage({
        requestId,
        action,
        ...params,
      });
    } catch (err) {
      clearTimeout(timer);
      delete registry[requestId];
      if (typeof window.__iapDebug === 'function') {
        window.__iapDebug(`postMessage EXCEPTION: ${String(err).slice(0, 160)}`);
      }
      resolve({ available: true, ok: false, error: String(err) });
    }
  });
}

/**
 * Fetch the localized product catalog from StoreKit.
 * Returns { available, ok, products: [{id, priceLocalized, title, description}] }
 */
export function getProducts(productIds) {
  return sendToNative('getProducts', { productIds });
}

/**
 * Initiate an Apple purchase for the given product ID. On success the
 * native layer returns { signedTransaction, signedRenewalInfo } — the
 * exact strings expected by /api/apple/verify-transaction.
 *
 * On user cancellation: { ok: true, cancelled: true }
 * On Apple error:       { ok: false, error: '...' }
 */
export function purchase(productId) {
  return sendToNative('purchase', { productId });
}

/**
 * Restore previous purchases. Returns { signedTransactions: [jws, jws, ...] }.
 * The caller forwards these to /api/apple/restore for server verification.
 */
export function restore() {
  return sendToNative('restore');
}

/**
 * Called by the native layer to deliver a result. Exposed on window so
 * Swift code can call it via `webView.evaluateJavaScript`.
 */
if (typeof window !== 'undefined') {
  window.__selahIAPDeliver = function (requestId, result) {
    const registry = window.__selahIAPResolvers || {};
    const resolver = registry[requestId];
    if (typeof resolver === 'function') {
      resolver(result);
    }
  };
}
